import React, { Component } from "react";
import { connect } from "react-redux";
import compose from "recompose/compose";
import { withStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import InputBase from "@material-ui/core/InputBase";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Typography from "@material-ui/core/Typography";
import LocationIcon from "@material-ui/icons/LocationOn";
import ResetIcon from "@material-ui/icons/SettingsBackupRestore";

import { SET_WAYPOINTS } from "../../../reducers/map";
import {
  pointsAlongLine,
  waypointsByBearingCond,
} from "../../../libs/geoLib";

// Stopover routing through many vias is slow and may shift geometry; cap it.
// Matches the TLS limit.
const WAYPOINT_LIMIT = 80;

const styles = (theme) => ({
  root: {
    position: "absolute",
    top: "10px",
    right: "10px",
    zIndex: 5,
  },
  panel: {
    textAlign: "center",
    padding: "4px 8px",
  },
  title: {
    textAlign: "left",
    fontWeight: 600,
  },
  searchInput: {
    display: "flex",
    alignItems: "center",
    width: 188,
    marginTop: "10px",
    minHeight: "44px",
    padding: "0 8px",
  },
  input: {
    marginLeft: theme.spacing.unit,
    flex: 1,
  },
  iconButton: {
    padding: 10,
  },
});

class AutoWaypoints extends Component {
  state = {
    isAuto: true,
    interval: "",
  };

  // Flatten the decoded leg paths (Google LatLng objects) into [[lat,lng], ...].
  getCoords = () => {
    const coords = [];
    (this.props.legs || []).forEach((leg) => {
      (leg.path || []).forEach((p) => {
        coords.push([p.lat(), p.lng()]);
      });
    });
    return coords;
  };

  onAutoChange = (e) => {
    this.setState({ isAuto: e.target.checked });
  };

  onIntervalChange = (e) => {
    // Reject non-numeric input (digits and a single decimal point only).
    const value = e.target.value.replace(/[^0-9.]/g, "");
    this.setState({ interval: value });
  };

  onGenerate = () => {
    const waypoints = this.props.waypoints || [];
    if (waypoints.length < 2) return;

    const coords = this.getCoords();
    if (coords.length < 2) return;

    let points;
    if (this.state.isAuto) {
      points = waypointsByBearingCond(coords);
    } else {
      const re = /^\d+(\.\d+)?$/;
      if (!re.test(this.state.interval)) {
        alert("Invalid input value");
        return;
      }
      points = pointsAlongLine(coords, Number(this.state.interval));
    }

    // Enforce the via-count cap; drop the overflow (there is no notistack).
    if (points.length > WAYPOINT_LIMIT) {
      points = points.slice(0, WAYPOINT_LIMIT);
      alert(
        "Too many waypoints. Only the first " +
          WAYPOINT_LIMIT +
          " were kept."
      );
    }

    const origin = waypoints[0];
    const destination = waypoints[waypoints.length - 1];
    const newWaypoints = [
      origin,
      ...points.map((p) => ({ position: { lat: p[0], lng: p[1] } })),
      destination,
    ];

    this.props.SET_WAYPOINTS(newWaypoints);
  };

  // Reset to default: drop all vias so the reroute sends none, and the backend
  // regenerates the default (by-distance) waypoints — loadNewPaths then syncs
  // them back onto the pins.
  onReset = () => {
    const waypoints = this.props.waypoints || [];
    if (waypoints.length < 2) return;
    const origin = waypoints[0];
    const destination = waypoints[waypoints.length - 1];
    this.props.SET_WAYPOINTS([origin, destination]);
  };

  render() {
    const { classes } = this.props;
    const { isAuto, interval } = this.state;

    return (
      <div className={classes.root}>
        <Paper className={classes.panel}>
          <Typography className={classes.title}>Waypoints Tool</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={isAuto}
                onChange={this.onAutoChange}
                color="primary"
              />
            }
            label="Auto"
            labelPlacement="end"
          />
          <Tooltip title="Generate waypoints">
            <IconButton
              color="primary"
              className={classes.iconButton}
              aria-label="generate"
              onClick={this.onGenerate}
            >
              <LocationIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reset to default waypoints">
            <IconButton
              color="primary"
              className={classes.iconButton}
              aria-label="reset to default"
              onClick={this.onReset}
            >
              <ResetIcon />
            </IconButton>
          </Tooltip>
        </Paper>
        {!isAuto && (
          <Paper className={classes.searchInput}>
            <InputBase
              className={classes.input}
              placeholder="Interval (km)"
              value={interval}
              inputProps={{ "aria-label": "interval km" }}
              onChange={this.onIntervalChange}
            />
          </Paper>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    legs: state.map.legs,
    waypoints: state.map.waypoints,
  };
};

const mapActionsToProps = {
  SET_WAYPOINTS: SET_WAYPOINTS,
};

export default compose(
  connect(mapStateToProps, mapActionsToProps),
  withStyles(styles)
)(AutoWaypoints);
